import React, {KeyboardEvent} from 'react';
import {OperatorGlossary, OperatorGlossaryData} from "types/Operator";
import {Form, Formik, FormikHelpers, FormikProps} from "formik";
import FormControls from "components/FormControls";
import {GlossaryEntryFieldSet} from "../../../components/Form/Form/Fieldset";
import * as Yup from "yup";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";

// Types ----

type GlossaryFormProps = {
  settings?: OperatorGlossary | null
  onSubmit: (values: OperatorGlossaryData, helpers: FormikHelpers<OperatorGlossaryData>) => any
}

// Helpers ----

const glossaryEntrySchema = Yup.object().shape({
  replacement: Yup.string().required('This field is required').max(250, "Maximum of 250 characters"),
  replacement_plural: Yup.string().required('This field is required').max(250, "Maximum of 250 characters"),
});

const validationSchema = Yup.object().shape({
  achievement: glossaryEntrySchema,
  collection: glossaryEntrySchema,
  event: glossaryEntrySchema,
  event_type: glossaryEntrySchema,
  exhibit: glossaryEntrySchema,
  exhibit_type: glossaryEntrySchema,
  facility: glossaryEntrySchema,
  facility_type: glossaryEntrySchema,
  fact: glossaryEntrySchema,
  trail: glossaryEntrySchema,
  sponsorship: glossaryEntrySchema,
  sponsor: glossaryEntrySchema,
  site: glossaryEntrySchema,
  zone: glossaryEntrySchema,
  visit: glossaryEntrySchema,
  quiz_type: glossaryEntrySchema,
  quiz: glossaryEntrySchema,
  membership: glossaryEntrySchema,
  membership_plan: glossaryEntrySchema,
  voucher: glossaryEntrySchema,
  ticket: glossaryEntrySchema,
  ticket_type: glossaryEntrySchema,
})

function getCurrentGlossary(settings: OperatorGlossary): OperatorGlossaryData {
  const fallback = {replacement: '', replacement_plural: ''}

  return {
    achievement: settings['achievement'] ?? fallback,
    collection: settings['collection'] ?? fallback,
    event: settings['event'] ?? fallback,
    event_type: settings['event_type'] ?? fallback,
    exhibit: settings['exhibit'] ?? fallback,
    exhibit_type: settings['exhibit_type'] ?? fallback,
    facility: settings['facility'] ?? fallback,
    facility_type: settings['facility_type'] ?? fallback,
    fact: settings['fact'] ?? fallback,
    trail: settings['trail'] ?? fallback,
    sponsorship: settings['sponsorship'] ?? fallback,
    sponsor: settings['sponsor'] ?? fallback,
    site: settings['site'] ?? fallback,
    zone: settings['zone'] ?? fallback,
    visit: settings['visit'] ?? fallback,
    quiz_type: settings['quiz_type'] ?? fallback,
    quiz: settings['quiz'] ?? fallback,
    membership: settings['membership'] ?? fallback,
    membership_plan: settings['membership_plan'] ?? fallback,
    voucher: settings['voucher'] ?? fallback,
    ticket: settings['ticket'] ?? fallback,
    ticket_type: settings['ticket_type'] ?? fallback,
  }
}

// Component ----

function GlossaryForm({onSubmit, settings}: GlossaryFormProps) {
  const initialValues = getCurrentGlossary(settings ?? {});

  function handleKeyboardSubmit(event: KeyboardEvent<HTMLFormElement>, form: FormikProps<any>): void {
    if (event.ctrlKey && event.key === 'Enter') {
      form.handleSubmit();
    }
  }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {formik => {
        return (
          <section aria-labelledby="glossary-heading">
            <Form onKeyDown={event => handleKeyboardSubmit(event, formik)}>
              <ErrorNotification formik={formik} />

              <div className="shadow sm:rounded-md">
                <div className="bg-white py-6 px-4 sm:p-6 sm:rounded-md">
                  <div>
                    <h2 id="glossary-heading" className="text-lg leading-6 font-medium text-gray-900">
                      Glossary
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                      Rename key terms used throughout the system
                    </p>
                  </div>

                  {/* Event Terms */}
                  <div className="mt-8">
                    <h3 className="leading-4 font-medium text-gray-900">Scheduled Event Terms</h3>

                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 sm:gap-x-6">
                      <GlossaryEntryFieldSet term={'event'} />
                      <GlossaryEntryFieldSet term={'event type'} />
                    </div>
                  </div>

                  {/* Content Terms */}
                  <div className="mt-8">
                    <h3 className="leading-4 font-medium text-gray-900">Content Terms</h3>

                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 sm:gap-x-6">
                      <GlossaryEntryFieldSet term={'collection'} />
                      <GlossaryEntryFieldSet term={'exhibit'} />
                      <GlossaryEntryFieldSet term={'exhibit type'} />
                      <GlossaryEntryFieldSet term={'fact'} />
                      <GlossaryEntryFieldSet term={'facility'} />
                      <GlossaryEntryFieldSet term={'facility type'} />
                      <GlossaryEntryFieldSet term={'trail'} />
                    </div>
                  </div>

                  {/* Sponsorship Terms */}
                  <div className="mt-8">
                    <h3 className="leading-4 font-medium text-gray-900">Sponsorship Terms</h3>

                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 sm:gap-x-6">
                      <GlossaryEntryFieldSet term={'sponsorship'} />
                      <GlossaryEntryFieldSet term={'sponsor'} />
                    </div>
                  </div>

                  {/* Location Terms */}
                  <div className="mt-8">
                    <h3 className="leading-4 font-medium text-gray-900">Location Terms</h3>

                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 sm:gap-x-6">
                      <GlossaryEntryFieldSet term={'site'} />
                      <GlossaryEntryFieldSet term={'zone'} />
                    </div>
                  </div>

                  {/* Engagement Terms */}
                  <div className="mt-8">
                    <h3 className="leading-4 font-medium text-gray-900">Engagement Terms</h3>

                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 sm:gap-x-6">
                      <GlossaryEntryFieldSet term={'achievement'} />
                      <GlossaryEntryFieldSet term={'visit'} />
                    </div>
                  </div>

                  {/* Education Terms */}
                  <div className="mt-8">
                    <h3 className="leading-4 font-medium text-gray-900">Education Terms</h3>

                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 sm:gap-x-6">
                      <GlossaryEntryFieldSet term={'quiz'} />
                      <GlossaryEntryFieldSet term={'quiz type'} />
                    </div>
                  </div>

                  {/* Wallet Terms */}
                  <div className="mt-8">
                    <h3 className="leading-4 font-medium text-gray-900">Wallet Terms</h3>

                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 sm:gap-x-6">
                      <GlossaryEntryFieldSet term={'membership'} />
                      <GlossaryEntryFieldSet term={'membership_plan'} />
                      <GlossaryEntryFieldSet term={'voucher'} />
                      <GlossaryEntryFieldSet term={'ticket'} />
                      <GlossaryEntryFieldSet term={'ticket_type'} />
                    </div>
                  </div>
                </div>

                <FormControls formik={formik} compact withoutCancel/>
              </div>
            </Form>
          </section>
        )
      }}
    </Formik>
  );
}

export default GlossaryForm;